import type { GeoCoordinates } from './assetGeo';
import { buildGoogleGeocodeJsonUrl, isGoogleGeocodeAvailable } from './googleGeocodeUrl';
import { resolveGeocodeProxyBaseUrl } from './geocodeProxyBaseUrl';

export type GeocodeResult = GeoCoordinates & { postalCode?: string };

export type GeocodeAddressOptions = {
  /** Official Hebrew settlement name — scopes geocoding to the locality, not a homonym street. */
  settlementHe?: string | null;
  /** Sub-district (nafa) for locality-only fallback queries. */
  settlementNafa?: string | null;
  /** Street + building for structured provider lookups. */
  streetLine?: string | null;
};

type NominatimAddress = {
  postcode?: string;
  city?: string;
  town?: string;
  village?: string;
  hamlet?: string;
  municipality?: string;
  suburb?: string;
};

export type NominatimResult = {
  lat?: string;
  lon?: string;
  display_name?: string;
  address?: NominatimAddress;
};

type PhotonFeature = {
  geometry?: { coordinates?: [number, number] };
  properties?: Record<string, unknown>;
};

type GoogleAddressComponent = {
  long_name?: string;
  short_name?: string;
  types?: string[];
};

type GoogleGeocodeResponse = {
  results?: Array<{
    geometry?: { location?: { lat?: number; lng?: number } };
    address_components?: GoogleAddressComponent[];
  }>;
  status?: string;
};

function nominatimBaseUrl(): string {
  return resolveGeocodeProxyBaseUrl();
}

function nominatimResultToGeocode(hit: NominatimResult): GeocodeResult | null {
  const lat = Number(hit.lat);
  const lng = Number(hit.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  const postalCode = extractPostalCodeFromNominatimResult(hit) ?? undefined;
  return { lat, lng, postalCode };
}

/** True when Nominatim's locality fields match the selected Israeli settlement. */
export function nominatimHitMatchesSettlement(hit: NominatimResult, settlementHe: string): boolean {
  const needle = settlementHe.trim();
  if (!needle) {
    return true;
  }
  const displayName = hit.display_name?.trim();
  if (displayName && (displayName.includes(needle) || needle.includes(displayName))) {
    return true;
  }
  const addr = hit.address;
  if (!addr) {
    return false;
  }
  const localityFields = [addr.city, addr.town, addr.village, addr.hamlet, addr.municipality, addr.suburb];
  for (const field of localityFields) {
    if (typeof field !== 'string') {
      continue;
    }
    const value = field.trim();
    if (!value) {
      continue;
    }
    if (value === needle || value.includes(needle) || needle.includes(value)) {
      return true;
    }
  }
  return false;
}

export function photonFeatureMatchesSettlement(
  properties: Record<string, unknown>,
  settlementHe: string,
): boolean {
  const needle = settlementHe.trim();
  if (!needle) {
    return true;
  }
  const city = typeof properties.city === 'string' ? properties.city.trim() : '';
  if (!city) {
    return false;
  }
  return city === needle || city.includes(needle) || needle.includes(city);
}

export function extractPostalCodeFromGoogleGeocode(data: GoogleGeocodeResponse): string | null {
  const components = data.results?.[0]?.address_components;
  if (!Array.isArray(components)) {
    return null;
  }
  for (const component of components) {
    if (!Array.isArray(component.types) || !component.types.includes('postal_code')) {
      continue;
    }
    const value = component.long_name?.trim() || component.short_name?.trim();
    if (value) {
      return value;
    }
  }
  return null;
}

export function extractPostalCodeFromNominatimResult(hit: NominatimResult): string | null {
  const postcode = hit.address?.postcode?.trim();
  return postcode || null;
}

/** Prefer city/town, then smaller settlement fields from Nominatim reverse results. */
export function extractLocalityFromNominatimResult(hit: NominatimResult): string | null {
  const addr = hit.address;
  if (!addr) {
    return null;
  }
  for (const field of [addr.city, addr.town, addr.village, addr.hamlet, addr.municipality]) {
    const value = typeof field === 'string' ? field.trim() : '';
    if (value) {
      return value;
    }
  }
  return null;
}

/** Fill missing postal code via Nominatim reverse geocoding (e.g. after Photon street hits). */
export async function enrichGeocodeResultPostalCode(hit: GeocodeResult): Promise<GeocodeResult> {
  if (hit.postalCode?.trim()) {
    return hit;
  }
  const postalCode = await reverseGeocodePostalCodeWithNominatim(hit.lat, hit.lng);
  return postalCode ? { ...hit, postalCode } : hit;
}

async function reverseGeocodeWithNominatim(
  lat: number,
  lng: number,
  acceptLanguage: 'he' | 'en',
): Promise<NominatimResult | null> {
  const url =
    `${nominatimBaseUrl()}/reverse?format=json&lat=${encodeURIComponent(String(lat))}` +
    `&lon=${encodeURIComponent(String(lng))}&addressdetails=1&accept-language=${acceptLanguage}`;
  try {
    const res = await fetch(url, {
      headers: {
        Accept: 'application/json',
      },
    });
    if (!res.ok) {
      return null;
    }
    return (await res.json()) as NominatimResult;
  } catch {
    return null;
  }
}

async function reverseGeocodePostalCodeWithNominatim(lat: number, lng: number): Promise<string | null> {
  const hit = await reverseGeocodeWithNominatim(lat, lng, 'he');
  return hit ? extractPostalCodeFromNominatimResult(hit) : null;
}

/** City/town (or village) label for the given coordinates via Nominatim reverse. */
export async function reverseGeocodeLocalityName(
  lat: number,
  lng: number,
  acceptLanguage: 'he' | 'en' = 'he',
): Promise<string | null> {
  const hit = await reverseGeocodeWithNominatim(lat, lng, acceptLanguage);
  return hit ? extractLocalityFromNominatimResult(hit) : null;
}

async function readNominatimRows(res: Response): Promise<NominatimResult[] | null> {
  if (!res.ok) {
    return null;
  }
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return null;
  }
  try {
    const parsed = (await res.json()) as unknown;
    return Array.isArray(parsed) ? (parsed as NominatimResult[]) : null;
  } catch {
    return null;
  }
}

/** Ignore HTML/error pages (e.g. nginx SPA fallback when `/geocode-proxy` is not mounted). */
async function readGoogleGeocodeResponse(res: Response): Promise<GoogleGeocodeResponse | null> {
  if (!res.ok) {
    return null;
  }
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return null;
  }
  try {
    return (await res.json()) as GoogleGeocodeResponse;
  } catch {
    return null;
  }
}

function googleGeocodeResponseToResult(data: GoogleGeocodeResponse): GeocodeResult | null {
  const loc = data.results?.[0]?.geometry?.location;
  if (typeof loc?.lat !== 'number' || typeof loc?.lng !== 'number') {
    return null;
  }
  const postalCode = extractPostalCodeFromGoogleGeocode(data) ?? undefined;
  return { lat: loc.lat, lng: loc.lng, postalCode };
}

async function geocodeWithGoogle(query: string): Promise<GeocodeResult | null> {
  const params = new URLSearchParams({ address: query });
  const url = buildGoogleGeocodeJsonUrl(params);
  if (!url) {
    return null;
  }
  const res = await fetch(url);
  const data = await readGoogleGeocodeResponse(res);
  if (!data) {
    return null;
  }
  return googleGeocodeResponseToResult(data);
}

async function geocodeWithGoogleSettlement(
  streetLine: string,
  settlementHe: string,
): Promise<GeocodeResult | null> {
  const components = `locality:${settlementHe}|country:IL`;
  const params = new URLSearchParams({
    address: streetLine,
    components,
  });
  const url = buildGoogleGeocodeJsonUrl(params);
  if (!url) {
    return null;
  }
  const res = await fetch(url);
  const data = await readGoogleGeocodeResponse(res);
  if (!data) {
    return null;
  }
  return googleGeocodeResponseToResult(data);
}

async function geocodeWithNominatim(query: string, settlementHe?: string | null): Promise<GeocodeResult | null> {
  const limit = settlementHe ? 5 : 1;
  const url =
    `${nominatimBaseUrl()}/search?format=json&limit=${limit}&addressdetails=1` +
    `&accept-language=he&q=${encodeURIComponent(query)}`;
  const res = await fetch(url, {
    headers: {
      Accept: 'application/json',
    },
  });
  const rows = await readNominatimRows(res);
  if (!rows || rows.length === 0) {
    return null;
  }
  const settlement = settlementHe?.trim();
  const hit =
    settlement
      ? rows.find((row) => nominatimHitMatchesSettlement(row, settlement)) ?? null
      : rows[0];
  if (!hit) {
    return null;
  }
  return nominatimResultToGeocode(hit);
}

async function geocodeWithNominatimStructured(
  streetLine: string,
  settlementHe: string,
): Promise<GeocodeResult | null> {
  const params = new URLSearchParams({
    format: 'json',
    limit: '5',
    addressdetails: '1',
    'accept-language': 'he',
    countrycodes: 'il',
    city: settlementHe,
    street: streetLine,
  });
  const url = `${nominatimBaseUrl()}/search?${params.toString()}`;
  const res = await fetch(url, {
    headers: {
      Accept: 'application/json',
    },
  });
  const rows = await readNominatimRows(res);
  if (!rows || rows.length === 0) {
    return null;
  }
  const hit =
    rows.find((row) => nominatimHitMatchesSettlement(row, settlementHe)) ?? rows[0];
  return nominatimResultToGeocode(hit);
}

async function geocodeWithPhoton(query: string, settlementHe?: string | null): Promise<GeocodeResult | null> {
  const trimmed = query.trim();
  if (!trimmed) {
    return null;
  }
  try {
    const url = `https://photon.komoot.io/api/?limit=8&q=${encodeURIComponent(trimmed)}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) {
      return null;
    }
    const contentType = res.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
      return null;
    }
    const data = (await res.json()) as { features?: PhotonFeature[] };
    const features = data.features;
    if (!Array.isArray(features) || features.length === 0) {
      return null;
    }
    const settlement = settlementHe?.trim();
    const feature = settlement
      ? features.find((row) => photonFeatureMatchesSettlement(row.properties ?? {}, settlement))
      : features[0];
    if (!feature?.geometry?.coordinates) {
      return null;
    }
    const [lng, lat] = feature.geometry.coordinates;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }
    return { lat, lng };
  } catch {
    return null;
  }
}

async function geocodeSettlementLocality(
  settlementHe: string,
  settlementNafa?: string | null,
): Promise<GeocodeResult | null> {
  const query = [settlementHe, settlementNafa?.trim(), 'ישראל'].filter(Boolean).join(', ');
  const fromPhoton = await geocodeWithPhoton(query, settlementHe);
  if (fromPhoton) {
    return fromPhoton;
  }
  return geocodeWithNominatim(query, settlementHe);
}

async function finalizeGeocodeHit(hit: GeocodeResult | null): Promise<GeocodeResult | null> {
  if (!hit) {
    return null;
  }
  return enrichGeocodeResultPostalCode(hit);
}

/** Resolve a free-text address to coordinates and optional postal code (Google if keyed, else Nominatim). */
export async function geocodeAddressDetails(
  query: string,
  options?: GeocodeAddressOptions,
): Promise<GeocodeResult | null> {
  const trimmed = query.trim();
  const settlementHe = options?.settlementHe?.trim() || null;
  const settlementNafa = options?.settlementNafa?.trim() || null;
  const streetLine = options?.streetLine?.trim() || null;
  const googleGeocodeEnabled = isGoogleGeocodeAvailable();

  if (settlementHe && streetLine) {
    if (googleGeocodeEnabled) {
      const fromGoogle = await geocodeWithGoogleSettlement(streetLine, settlementHe);
      if (fromGoogle) {
        return finalizeGeocodeHit(fromGoogle);
      }
    }
    const fromStructured = await geocodeWithNominatimStructured(streetLine, settlementHe);
    if (fromStructured) {
      return finalizeGeocodeHit(fromStructured);
    }
    const photonStreetQuery = `${streetLine} ${settlementHe}`;
    const fromPhotonStreet = await geocodeWithPhoton(photonStreetQuery, settlementHe);
    if (fromPhotonStreet) {
      return finalizeGeocodeHit(fromPhotonStreet);
    }
  }

  if (trimmed) {
    if (googleGeocodeEnabled) {
      const fromGoogle = await geocodeWithGoogle(trimmed);
      if (fromGoogle) {
        return finalizeGeocodeHit(fromGoogle);
      }
    }
    const fromNominatim = await geocodeWithNominatim(trimmed, settlementHe);
    if (fromNominatim) {
      return finalizeGeocodeHit(fromNominatim);
    }
    if (settlementHe) {
      const fromPhoton = await geocodeWithPhoton(trimmed, settlementHe);
      if (fromPhoton) {
        return finalizeGeocodeHit(fromPhoton);
      }
    }
  }

  if (settlementHe) {
    return finalizeGeocodeHit(await geocodeSettlementLocality(settlementHe, settlementNafa));
  }

  return null;
}

/** @deprecated Prefer {@link geocodeAddressDetails} when postal code is needed. */
export async function geocodeAddressQuery(query: string): Promise<GeoCoordinates | null> {
  const hit = await geocodeAddressDetails(query);
  if (!hit) {
    return null;
  }
  return { lat: hit.lat, lng: hit.lng };
}
